import { AgentChatData, useAgentChat, useMadSdk } from "@madhive/mad-hooks";
import {
  Button,
  ButtonGroup,
  Chat,
  type ChatMessageProps,
  DetailsSummary,
  Drawer,
  FileCard,
  Icon,
  Markdown,
  withDisplayName
} from "@madhive/kit-ui";
import { AgentChatDrawerProps, AgentChatProps } from "./types";
import { AiFeedback } from "./AgentChat.Feedback";
import {
  useCallback,
  useEffect,
  useImperativeHandle,
  useMemo,
  useRef,
  useState
} from "react";
import { styles } from "./styles";
import { useMadTracker } from "../../hooks";
import AgentChatPrompter from "./AgentChat.Prompter";
import { AgentChatError } from "./AgentChat.Error";
import { createMakeInfoDrawer } from "./AgentChat.makeInfoDrawer";
import {
  AgentDataParseError,
  type AgentName,
  type AgentSessionEvent,
  type AgentSessionUpload,
  type AgentEventContentPart
} from "@madhive/mad-sdk";

const DEFAULT_CHAT_SESSION_ID = "chat:general";

/**
 * `AgentChat` is a wrapper around the kit-ui `Chat` component and mad-hooks `useAgentChat` hook
 * that provides an interface for AI-powered chat interactions.
 */
function AgentChat<Agent extends AgentName = AgentName>({
  agent,
  cues,
  sessionId = DEFAULT_CHAT_SESSION_ID,
  onMessage,
  feedbackProps = {},
  inputProps: {
    placeholder = "Ask a follow-up question...",
    description = "Maverick is still learning – Make sure to double-check outputs.",
    ...inputProps
  } = {},
  emptyState = <DefaultEmptyState />,
  context,
  ensureSession,
  renderEventData,
  onEventData,
  storage,
  ttl,
  lazy = false,
  stateRef,
  renderEventAttachments,
  renderEventThoughts,
  lookupMessageTitle,
  attachmentsPosition = "top",
  uploaderProps: _uploaderProps,
  autoFillHeight = true,
  children,
  headerRef
}: AgentChatProps<Agent>) {
  const [currentSessionId, setCurrentSessionId] = useState(sessionId);
  const mountedAtTime = useRef(Date.now());
  const latestTrackingContextRef = useRef({
    sessionId,
    sessionIdResolved: ""
  });

  useEffect(() => {
    setCurrentSessionId(sessionId);
  }, [sessionId]);

  const {
    session,
    messages,
    status,
    error,
    sendMessage,
    reload,
    newSession,
    abort,
    activate,
    isCreatingSession
  } = useAgentChat(agent, currentSessionId, {
    context,
    ensureSession,
    onEventData,
    storage,
    ttl,
    lazy,
    // Sync state once a proxy id resolves to a real session id; leave it alone if we're
    // already using a real id (e.g. re-created after expiring) so we don't clobber it.
    onNewSession: currentSessionId?.includes(":")
      ? id => setCurrentSessionId(id)
      : undefined
  });

  useImperativeHandle(
    stateRef,
    () => ({
      sessionId: session?.id || currentSessionId,
      messages,
      session
    }),
    [currentSessionId, messages, session]
  );

  const madSdk = useMadSdk();

  const agentHandler = madSdk.ai.agents[agent];

  const uploaderProps = useMemo<
    Exclude<Required<AgentChatProps<Agent>>["uploaderProps"], true> | undefined
  >(() => {
    if (!_uploaderProps) {
      return undefined;
    }

    const uploadFn = agentHandler.uploads.save;
    let processFile: ((file: File) => Promise<AgentSessionUpload>) | undefined =
      undefined;
    if (uploadFn) {
      processFile = async (file: File) => {
        const id = await activate();
        return uploadFn({
          sessionId: id,
          file
        });
      };
    }
    return {
      ...(_uploaderProps === true ? {} : _uploaderProps),
      processFile
    };
  }, [_uploaderProps, agentHandler, session?.id, currentSessionId, activate]);

  const madTracker = useMadTracker();
  const trackedErrorsRef = useRef(new Set<Error>());

  latestTrackingContextRef.current = {
    sessionId,
    sessionIdResolved: session?.id || ""
  };

  useEffect(() => {
    if (!madTracker) {
      return undefined;
    }

    const trackStartedCompletedEvents = (
      event: "Started" | "Completed",
      data: Record<string, string> = {}
    ) => {
      const trackingContext = latestTrackingContextRef.current;
      const { sessionId: trackedClientKey, sessionIdResolved } =
        trackingContext;

      madTracker.track(`Maverick Agent Interaction ${event}`, {
        dataAgent: agent,
        dataClientKey: trackedClientKey,
        dataSessionId: sessionIdResolved,
        ...data
      });
    };

    trackStartedCompletedEvents("Started");

    return () => {
      trackStartedCompletedEvents("Completed", {
        stopwatchDuration: String(Date.now() - mountedAtTime.current)
      });
    };
  }, [madTracker]);

  useEffect(() => {
    if (
      error instanceof AgentDataParseError &&
      madTracker &&
      !trackedErrorsRef.current.has(error)
    ) {
      trackedErrorsRef.current.add(error);
      madTracker.track("Maverick Agent Parse Error", {
        dataAgent: agent,
        dataClientKey: sessionId,
        dataSessionId: session?.id || "",
        dataError: error.message
      });
    }
  }, [error]);

  const handleSendMessage = useCallback(
    (message: { text?: string; attachments?: AgentSessionUpload[] }) => {
      if (!message?.text?.trim() && !message?.attachments?.length) {
        return;
      }
      sendMessage(message.text || "", { attachments: message.attachments });
      onMessage?.(message.text || "", { attachments: message.attachments });
    },
    [onMessage, sendMessage]
  );

  function onCueClick(message: string) {
    handleSendMessage({ text: message });
    cues?.onClick?.(message);
    if (madTracker) {
      madTracker.track("Maverick Agent Prompt", {
        dataAgent: agent,
        dataClientKey: sessionId,
        dataSessionId: session?.id || "",
        dataPromptMessage: message
      });
    }
  }

  const lookupBottomActions = useCallback(
    (message: (typeof messages)[number]) => {
      if (message.role === "user") {
        if (!message.errorMessage) {
          return null;
        }
        return (
          <ButtonGroup justifyContent="end">
            <Button
              onClick={() =>
                handleSendMessage({ text: contentText(message.content.parts) })
              }
              variant="ghost"
              prefix={<Icon name="Reset" tooltip="Retry" />}
              size="small"
              ariaLabel="Retry message"
            />
          </ButtonGroup>
        );
      }

      if (!session || message.author === "system") {
        return null;
      }
      return (
        <AiFeedback
          {...feedbackProps}
          agent={agent}
          clientKey={sessionId}
          session={session}
          message={message}
        />
      );
    },
    [feedbackProps, session, sessionId, handleSendMessage]
  );

  const inputAreaRef = useRef<HTMLDivElement>(null);

  return (
    <Chat
      theme="maverick"
      autoFillHeight={autoFillHeight}
      headerRef={headerRef}
      footerRef={inputAreaRef}
    >
      {children}
      <Chat.Messages
        messages={messages}
        isWaitingForResponse={status === "processing"}
        lookupSortValue={({ timestamp }) => timestamp || 0}
        emptyState={status !== "error" && emptyState}
        renderMessage={(event, index) => (
          <AgentMessage
            event={event}
            index={index}
            bottomActions={lookupBottomActions(event)}
            renderEventData={renderEventData}
            renderEventAttachments={renderEventAttachments}
            renderEventThoughts={renderEventThoughts}
            attachmentsPosition={attachmentsPosition}
            lookupMessageTitle={lookupMessageTitle}
            isLoading={status === "processing" && index === messages.length - 1}
            session={session}
          />
        )}
        cues={
          cues?.options?.length
            ? {
                delay: messages.length ? 3_000 : 0,
                ...cues,
                options: cues.options,
                onClick: onCueClick
              }
            : undefined
        }
      >
        {status === "error" && (
          <AgentChatError
            error={error || new Error("An error occurred.")}
            reloadSession={reload}
            newSession={newSession}
            isCreatingSession={isCreatingSession}
          />
        )}
      </Chat.Messages>

      <div
        className="agent-chat-input"
        ref={inputAreaRef}
        css={styles.input.base}
      >
        {!inputProps?.noTextarea && (
          <Chat.Input<AgentSessionUpload>
            onSubmit={handleSendMessage}
            disabled={status === "processing"}
            placeholder={placeholder}
            onAbort={abort}
            uploaderProps={uploaderProps}
          />
        )}
        <span css={styles.input.description}>{description}</span>
      </div>
    </Chat>
  );
}

/**
 * `AgentChat.Drawer` is a wrapper around the `AgentChat` component that renders it within a `Drawer` component from kit-ui.
 */
function AgentChatDrawer<Agent extends AgentName>({
  drawerProps: { title = "Maverick Training", titleIcon, ...drawerProps } = {},
  children: pushContent,
  ...chatProps
}: AgentChatDrawerProps<Agent>) {
  const [isOpen, setIsOpen] = useState(!!drawerProps.isOpen);

  const target = useMemo(() => {
    if (drawerProps?.target) {
      return drawerProps.target;
    }
    if (drawerProps.isOpen === undefined) {
      return <Icon name="Spark" size="medium" />;
    }
    return null;
  }, [drawerProps?.target, drawerProps.isOpen]);

  function handleOnClose() {
    if (drawerProps.isOpen === undefined) {
      setIsOpen(false);
    }
    drawerProps?.onClose?.();
  }

  function handleOnOpen() {
    if (drawerProps.isOpen === undefined) {
      setIsOpen(true);
    }
    drawerProps?.onOpen?.();
  }

  useEffect(() => {
    setIsOpen(!!drawerProps.isOpen);
  }, [drawerProps.isOpen]);

  const headerRef = useRef<HTMLDivElement>(null);

  return (
    <Drawer
      {...drawerProps}
      target={target}
      isOpen={isOpen}
      noHeader
      onClose={handleOnClose}
      onOpen={handleOnOpen}
      noContentPadding
      pushContent={pushContent}
      theme="maverick"
    >
      <AgentChat {...chatProps} headerRef={headerRef}>
        <Chat.Header
          onClose={handleOnClose}
          elemRef={headerRef}
          prefix={titleIcon}
        >
          {title}
        </Chat.Header>
      </AgentChat>
    </Drawer>
  );
}

/**
 * Default empty state component for the `AgentChat`.
 * This is used when no `emptyState` prop is provided.
 */
function DefaultEmptyState() {
  return (
    <div css={styles.chat.emptyState}>
      Instant answers.
      <br /> Deeper dives.
    </div>
  );
}

/**
 * `AgentMessage` is a helper component that renders a single chat message(s) based on an `AgentSessionEvent`.
 * It handles rendering both the `content` and `data` of the event, as well as any associated errors.
 */
function AgentMessage<Agent extends AgentName>({
  event,
  bottomActions,
  renderEventData,
  renderEventAttachments = ({ attachments }) => {
    if (!attachments?.length) {
      return null;
    }
    return attachments.map(({ fileName }) => (
      <FileCard key={fileName} file={{ name: fileName }} />
    ));
  },
  attachmentsPosition,
  lookupMessageTitle,
  index,
  isLoading,
  renderEventThoughts = event => (
    <Chat.Message received={event.role !== "user"}>
      <DetailsSummary
        title="Agent Thoughts"
        isOpen={isLoading}
        variant="inline"
      >
        <Markdown>{thoughtsText(event.content.parts)}</Markdown>
      </DetailsSummary>
    </Chat.Message>
  ),
  session
}: AgentMessageProps<Agent>) {
  const title =
    event.role !== "user" ? lookupMessageTitle?.(event, index) : undefined;
  const text = contentText(event.content.parts);
  return (
    <>
      {!!renderEventThoughts &&
        !!thoughtsText(event.content.parts) &&
        renderEventThoughts(event)}
      {(!!text || (!!event.attachments && event.attachments.length)) && (
        <Chat.Message
          received={event.role !== "user"}
          type="markdown"
          title={title}
          bottomActions={!event.data && bottomActions}
          titleIcon={<Icon name="Spark" size="medium" styled />}
          content={text}
          attachments={renderEventAttachments?.(event)}
          attachmentsPosition={attachmentsPosition}
          icon={
            !!event.errorMessage && (
              <Icon
                name="Circled.Exclamation"
                fill="var(--color-danger)"
                tooltip={event.errorMessage}
              />
            )
          }
        />
      )}

      {!!(event.data && renderEventData) && (
        <Chat.Message
          received={event.role !== "user"}
          title={!text ? title : undefined}
          bottomActions={bottomActions}
          titleIcon={<Icon name="Spark" size="medium" styled />}
          icon={
            !text &&
            !!event.errorMessage && (
              <Icon
                name="Circled.Exclamation"
                fill="var(--color-danger)"
                tooltip={event.errorMessage}
              />
            )
          }
        >
          {renderEventData(event.data, {
            event,
            session
          })}
        </Chat.Message>
      )}
    </>
  );
}

type AgentMessageProps<Agent extends AgentName> = {
  /**
   * The `AgentSessionEvent` that contains the data for this message.
   * This includes the content, attachments, data, and any errors associated with the event.
   */
  event: AgentSessionEvent<Agent>;
  /** The index of the event in the list of messages. */
  index: number;
  /** Indicates if the message is currently loading. */
  isLoading?: boolean;
  /** The current session data for the chat. */
  session: AgentChatData<Agent>["session"];
} & Pick<
  AgentChatProps<Agent>,
  | "renderEventData"
  | "renderEventAttachments"
  | "attachmentsPosition"
  | "lookupMessageTitle"
  | "renderEventThoughts"
> &
  Pick<ChatMessageProps, "bottomActions">;

/** Joins an event's non-thought content parts into plain text for display/retry. */
function contentText(parts: AgentEventContentPart[] | undefined): string {
  return (parts || [])
    .filter(part => !part.thought)
    .map(part => part.text || "")
    .join("");
}

/** Joins an event's thought parts into plain text, e.g. for `renderEventThoughts`. */
function thoughtsText(parts: AgentEventContentPart[] | undefined): string {
  return (parts || [])
    .filter(part => !!part.thought)
    .map(part => part.text || "")
    .join("");
}

AgentChat.Drawer = withDisplayName(AgentChatDrawer, "AgentChat.Drawer");
AgentChat.Prompter = withDisplayName(AgentChatPrompter, "AgentChat.Prompter");
AgentChat.makeInfoDrawer = createMakeInfoDrawer(AgentChatDrawer);

export default AgentChat;
